import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  seriesService,
  Series,
  CreateSeriesDto,
  UpdateSeriesDto,
} from '../services/seriesService';

interface SeriesState {
  // State
  allSeries: Series[];
  mySeries: Series[];
  selectedSeries: Series | null;
  loading: boolean;
  error: string | null;

  // Actions
  fetchAllSeries: () => Promise<void>;
  fetchMySeries: () => Promise<void>;
  fetchSeriesById: (id: string) => Promise<void>;
  createSeries: (data: CreateSeriesDto) => Promise<Series>;
  updateSeries: (id: string, data: UpdateSeriesDto) => Promise<Series>;
  deleteSeries: (id: string) => Promise<void>;
  addCourseToSeries: (seriesId: string, courseId: string, order?: number) => Promise<void>;
  removeCourseFromSeries: (seriesId: string, courseId: string) => Promise<void>;
  reorderCourses: (seriesId: string, courseIds: string[]) => Promise<void>;

  // Clear state
  clearError: () => void;
  clearSelectedSeries: () => void;
}

export const useSeriesStore = create<SeriesState>()(
  devtools(
    (set, get) => ({
      // Initial state
      allSeries: [],
      mySeries: [],
      selectedSeries: null,
      loading: false,
      error: null,

      // Actions
      fetchAllSeries: async () => {
        set({ loading: true, error: null });
        try {
          const allSeries = await seriesService.getAll();
          set({ allSeries, loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch series',
          });
        }
      },

      fetchMySeries: async () => {
        set({ loading: true, error: null });
        try {
          const mySeries = await seriesService.getMySeries();
          set({ mySeries, loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch my series',
          });
        }
      },

      fetchSeriesById: async (id: string) => {
        set({ loading: true, error: null });
        try {
          const series = await seriesService.getById(id);
          set({ selectedSeries: series, loading: false });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch series',
          });
        }
      },

      createSeries: async (data: CreateSeriesDto) => {
        set({ loading: true, error: null });
        try {
          const newSeries = await seriesService.create(data);
          const { mySeries } = get();
          set({
            mySeries: [newSeries, ...mySeries],
            loading: false,
          });
          return newSeries;
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to create series',
          });
          throw error;
        }
      },

      updateSeries: async (id: string, data: UpdateSeriesDto) => {
        set({ loading: true, error: null });
        try {
          const updatedSeries = await seriesService.update(id, data);
          const { mySeries, selectedSeries } = get();
          set({
            mySeries: mySeries.map((s) => (s.id === id ? updatedSeries : s)),
            selectedSeries: selectedSeries?.id === id ? updatedSeries : selectedSeries,
            loading: false,
          });
          return updatedSeries;
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to update series',
          });
          throw error;
        }
      },

      deleteSeries: async (id: string) => {
        set({ loading: true, error: null });
        try {
          await seriesService.delete(id);
          const { mySeries, selectedSeries } = get();
          set({
            mySeries: mySeries.filter((s) => s.id !== id),
            selectedSeries: selectedSeries?.id === id ? null : selectedSeries,
            loading: false,
          });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to delete series',
          });
          throw error;
        }
      },

      addCourseToSeries: async (seriesId: string, courseId: string, order?: number) => {
        set({ loading: true, error: null });
        try {
          const updatedSeries = await seriesService.addCourse(seriesId, {
            courseId,
            order,
          });
          const { mySeries, selectedSeries } = get();
          set({
            mySeries: mySeries.map((s) => (s.id === seriesId ? updatedSeries : s)),
            selectedSeries: selectedSeries?.id === seriesId ? updatedSeries : selectedSeries,
            loading: false,
          });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to add course to series',
          });
          throw error;
        }
      },

      removeCourseFromSeries: async (seriesId: string, courseId: string) => {
        set({ loading: true, error: null });
        try {
          const updatedSeries = await seriesService.removeCourse(seriesId, courseId);
          const { mySeries, selectedSeries } = get();
          set({
            mySeries: mySeries.map((s) => (s.id === seriesId ? updatedSeries : s)),
            selectedSeries: selectedSeries?.id === seriesId ? updatedSeries : selectedSeries,
            loading: false,
          });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to remove course from series',
          });
          throw error;
        }
      },

      reorderCourses: async (seriesId: string, courseIds: string[]) => {
        set({ loading: true, error: null });
        try {
          const updatedSeries = await seriesService.reorderCourses(seriesId, {
            courseIds,
          });
          const { mySeries, selectedSeries } = get();
          set({
            mySeries: mySeries.map((s) => (s.id === seriesId ? updatedSeries : s)),
            selectedSeries: selectedSeries?.id === seriesId ? updatedSeries : selectedSeries,
            loading: false,
          });
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to reorder courses',
          });
          throw error;
        }
      },

      clearError: () => {
        set({ error: null });
      },

      clearSelectedSeries: () => {
        set({ selectedSeries: null });
      },
    }),
    { name: 'SeriesStore' }
  )
);

// Utility hooks
export const useMySeriesActions = () =>
  useSeriesStore((state) => ({
    series: state.mySeries,
    loading: state.loading,
    error: state.error,
    fetch: state.fetchMySeries,
    create: state.createSeries,
    update: state.updateSeries,
    delete: state.deleteSeries,
  }));

export const useSeriesDetail = () =>
  useSeriesStore((state) => ({
    series: state.selectedSeries,
    loading: state.loading,
    error: state.error,
    fetch: state.fetchSeriesById,
    clear: state.clearSelectedSeries,
    addCourse: state.addCourseToSeries,
    removeCourse: state.removeCourseFromSeries,
    reorder: state.reorderCourses,
  }));
